import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import './SpeakerForm.sass';

import Input from '../Input';
import InputSocial from '../InputSocial';
import InputFile from '../InputFile';
import ErrorLabel from '../ErrorLabel';
import Textarea from '../Textarea';
import Checkbox from '../Checkbox';
import ButtonPrimary from '../../../uikit/Buttons/Primary';
import Select from '../Select';
import Tooltip from '../Tooltip';
import Radio from '../Radio';
import CityInput from '../CityInput';
import MultiSelect from '../MultiSelect';
import SuccessPopup from '../../SuccessPopup';

import {
	CAPTCHA_KEY,
	FORMS_ENDPOINTS,
	VALIDATION_PATTERNS,
	TALK_NAMES_ARRAY,
	FIRST_TALK_NAME_ONLY,
	SECOND_TALK_NAME_ONLY,
	THIRD_TALK_NAME_ONLY,
} from '../../../utils/constants';
import { constructDevSrc } from '../../../utils/helpers';

import IInputs from '../../../interfaces/IInputs';

import { ReactComponent as FacebookLogo } from '../../../assets/img/socials/fb.svg';
import { ReactComponent as LinkedinLogo } from '../../../assets/img/socials/linkedin.svg';
import { ReactComponent as TwitterLogo } from '../../../assets/img/socials/twitter.svg';
import { ReactComponent as RemoveIcon } from '../../../assets/img/forms/remove.svg';

interface Props {
	nameLabel: string;
	surnameLabel: string;
	emailLabel: string;
	companyNameLabel: string;
	registrationCityLabel: string;
	privacyPolicyLabelText: string;
	speakerTitleLabel: string;
	speakerBioLabel: string;
	speakerSocialMediaLabel: string;
	speakerPhotoLabel: string;
	speakerTalkTitle: string;
	speakerTalkNameLabel: string;
	speakerFormatLabel: string;
	speakerFormatTooltip: string;
	speakerLanguageLabel: string;
	speakerTrackLabel: string;
	speakerTrackTooltip: string;
	speakerAudienceLevelLabel: string;
	speakerTalkKeyPointsLabel: string;
	speakerTalkDescriptionLabel: string;
	speakerCoSpeakerLabel: string;
	speakerCodeOfConductAndSpeakerContractAgreementLabel: string;
	speakerAddTalkButtonLabel: string;
	submitButtonLabel: string;
	cityPlaceholder: string;
	speakerBioPlaceholder: string;
	speakerFacebookLinkPlaceholder: string;
	speakerLinkedinLinkPlaceholder: string;
	speakerTwitterLinkPlaceholder: string;
	speakerPhotoPlaceholder: string;
	speakerPhotoBottomDescription: string;
	speakerFormatPlaceholder: string;
	speakerLanguagePlaceholder: string;
	speakerTrackPlaceholder: string;
	speakerAudienceLevelPlaceholder: string;
	speakerTalkKeyPointsPlaceholder: string;
	speakerTalkDescriptionPlaceholder: string;
	speakerBioTooltip: string;
	speakerTalkNameTooltip: string;
	speakerTalkDescriptionTooltip: string;
	speakerLanguageTooltip: string;
	speakerFormatValues: [];
	speakerLanguageValues: [];
	speakerTrackValues: [];
	speakerAudienceLevelValues: [];
}

function SpeakerForm(props: Props) {
	const {
		nameLabel,
		surnameLabel,
		emailLabel,
		companyNameLabel,
		registrationCityLabel,
		privacyPolicyLabelText,
		speakerTitleLabel,
		speakerBioLabel,
		speakerSocialMediaLabel,
		speakerPhotoLabel,
		speakerTalkTitle,
		speakerTalkNameLabel,
		speakerFormatLabel,
		speakerFormatTooltip,
		speakerLanguageLabel,
		speakerTrackLabel,
		speakerTrackTooltip,
		speakerAudienceLevelLabel,
		speakerTalkKeyPointsLabel,
		speakerTalkDescriptionLabel,
		speakerCoSpeakerLabel,
		speakerCodeOfConductAndSpeakerContractAgreementLabel,
		speakerAddTalkButtonLabel,
		submitButtonLabel,
		cityPlaceholder,
		speakerBioPlaceholder,
		speakerFacebookLinkPlaceholder,
		speakerLinkedinLinkPlaceholder,
		speakerTwitterLinkPlaceholder,
		speakerPhotoPlaceholder,
		speakerPhotoBottomDescription,
		speakerFormatPlaceholder,
		speakerLanguagePlaceholder,
		speakerTrackPlaceholder,
		speakerAudienceLevelPlaceholder,
		speakerTalkKeyPointsPlaceholder,
		speakerTalkDescriptionPlaceholder,
		speakerFormatValues,
		speakerLanguageValues,
		speakerTrackValues,
		speakerAudienceLevelValues,
		speakerBioTooltip,
		speakerTalkNameTooltip,
		speakerTalkDescriptionTooltip,
		speakerLanguageTooltip,
	} = props;
	const {
		register,
		handleSubmit,
		formState: { errors, dirtyFields },
		setValue,
		clearErrors,
		watch,
		reset,
		unregister,
		getValues,
	} = useForm<IInputs>({ mode: 'onChange' });

	const watchAllFields = watch();
	const [isFull, setIsFull] = useState<boolean>(false);
	const [isSucceed, setIsSucceed] = useState<boolean>(false);
	const [isSending, setIsSending] = useState<boolean>(false);
	const [talkNumbers, setTalkNumbers] = useState<number>(1);

	useEffect(() => {
		isSucceed
			? document.body.classList.add('body-overflow-hidden')
			: document.body.classList.remove('body-overflow-hidden');
	}, [isSucceed]);

	const onClickHandler = () => {
		reset();
		setIsSucceed(false);
		setIsFull(false);
		setIsSending(false);
		window.location.reload();
	};

	const removeTalkInfo = () => {
		if (talkNumbers === 3) {
			/* @ts-ignore */
			unregister([...THIRD_TALK_NAME_ONLY]);
		} else {
			/* @ts-ignore */
			unregister([...SECOND_TALK_NAME_ONLY]);
		}
		setTalkNumbers(talkNumbers - 1);
	};

	useEffect(() => {
		const generalRequiredFields = [
			'name',
			'surname',
			'email',
			'title',
			'compony',
			'location',
			'bio',
			'speakerPhoto',
			'talkName',
			'talkFormat',
			'talkLanguage',
			'talkAudienceLevel',
			'codeConduct',
			'privacy',
		];
		let requiredFields: string[] = [];
		if (talkNumbers === 3) {
			requiredFields = [...generalRequiredFields, ...TALK_NAMES_ARRAY];
		} else if (talkNumbers === 2) {
			requiredFields = [...generalRequiredFields, ...SECOND_TALK_NAME_ONLY];
		} else {
			requiredFields = [...generalRequiredFields];
		}
		/* @ts-ignore */
		const allTrue = requiredFields.every((field) => {
			/* @ts-ignore */
			if (watchAllFields[field] != false) {
				return true;
			}
			return false;
		});
		if (allTrue && !isSending) {
			setIsFull(true);
		} else {
			setIsFull(false);
		}
	}, [watchAllFields, talkNumbers]);

	const resetPhoto = () => {
		/* @ts-ignore */
		reset({ ...getValues(), speakerPhoto: null });
	};

	const onSubmit = async (data: IInputs) => {
		const readyData = {};
		const firstTalkInfo = {};
		const secondTalkInfo = {};
		const thirdTalkInfo = {};
		for (const item in data) {
			if (!TALK_NAMES_ARRAY.includes(item)) {
				/* @ts-ignore */
				readyData[item] = data[item];
			} else if (FIRST_TALK_NAME_ONLY.includes(item)) {
				/* @ts-ignore */
				firstTalkInfo[item] = data[item];
			} else if (SECOND_TALK_NAME_ONLY.includes(item)) {
				/* @ts-ignore */
				secondTalkInfo[item.substring(0, item.length - 1)] = data[item];
			} else if (THIRD_TALK_NAME_ONLY.includes(item)) {
				/* @ts-ignore */
				thirdTalkInfo[item.substring(0, item.length - 1)] = data[item];
			}
		}
		/* @ts-ignore */
		readyData.talkInfo = [];
		if (Object.keys(firstTalkInfo).length !== 0) {
			/* @ts-ignore */
			readyData.talkInfo.push(firstTalkInfo);
		}
		if (Object.keys(secondTalkInfo).length !== 0) {
			/* @ts-ignore */
			readyData.talkInfo.push(secondTalkInfo);
		}
		if (Object.keys(thirdTalkInfo).length !== 0) {
			/* @ts-ignore */
			readyData.talkInfo.push(thirdTalkInfo);
		}

		const reader = new FileReader();
		if (data) {
			/* @ts-ignore */
			reader.readAsDataURL(data.speakerPhoto[0]);
		}
		let base64data: string | ArrayBuffer | null = '';
		reader.onloadend = () => {
			base64data = reader.result;
		};

		setIsSending(true);

		grecaptcha.ready(() => {
			grecaptcha
				.execute(CAPTCHA_KEY, {
					action: 'speaker',
				})
				.then((result) => {
					fetch(constructDevSrc(FORMS_ENDPOINTS.speakerForm), {
						method: 'POST',
						headers: {
							Accept: 'application/json',
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							'g-recaptcha-response': result,
							...readyData,
							speakerPhoto: base64data,
						}),
					})
						.then((response) => {
							if (response.status === 200) {
								setIsSucceed(true);
								setTalkNumbers(1);
								setIsSending(false);
							}
						})
						.catch((err) => {
							console.log(err.message);
						});
				});
		});
	};

	return (
		<>
			<form className="speaker-form" onSubmit={handleSubmit(onSubmit)}>
				<div className="speaker-form__section">
					<div className="form-row">
						<div className="form-element form-element--double">
							<Input
								inputName="name"
								label={nameLabel || 'Name'}
								invalid={!!errors.name}
								valid={
									watchAllFields.name !== '' && dirtyFields.name && !errors.name
								}
								required
								register={register}
								rules={{
									required: 'This field is required',
									minLength: {
										value: 2,
										message:
											'Minimum required length for this field is 2 characters',
									},
									pattern: {
										value: VALIDATION_PATTERNS.name,
										message: 'Entered value should contain characters only',
									},
								}}
							/>
							{errors.name && <ErrorLabel message={errors.name.message} />}
						</div>
						<div className="form-element form-element--double">
							<Input
								inputName="surname"
								label={surnameLabel || 'Surname'}
								invalid={!!errors.surname}
								valid={
									watchAllFields.surname !== '' &&
									dirtyFields.surname &&
									!errors.surname
								}
								required
								register={register}
								rules={{
									required: 'This field is required',
									minLength: {
										value: 2,
										message:
											'Minimum required length for this field is 2 characters',
									},
									pattern: {
										value: VALIDATION_PATTERNS.name,
										message: 'Entered value should contain characters only',
									},
								}}
							/>
							{errors.surname && (
								<ErrorLabel message={errors.surname.message} />
							)}
						</div>
					</div>
					<div className="form-row">
						<div className="form-element">
							<Input
								inputName="email"
								label={emailLabel || 'E-mail'}
								invalid={!!errors.email}
								valid={
									watchAllFields.email !== '' &&
									dirtyFields.email &&
									!errors.email
								}
								required
								register={register}
								rules={{
									required: 'This field is required',
									minLength: {
										value: 2,
										message:
											'Minimum required length for this field is 2 characters',
									},
									pattern: {
										value: VALIDATION_PATTERNS.email,
										message: 'Entered value does not match email format',
									},
								}}
							/>
							{errors.email && <ErrorLabel message={errors.email.message} />}
						</div>
					</div>
					<div className="form-row">
						<div className="form-element">
							<Input
								inputName="title"
								label={speakerTitleLabel || 'Title'}
								invalid={!!errors.title}
								valid={
									watchAllFields.title !== '' &&
									dirtyFields.title &&
									!errors.title
								}
								required
								register={register}
								rules={{
									required: 'This field is required',
									minLength: {
										value: 2,
										message:
											'Minimum required length for this field is 2 characters',
									},
								}}
							/>
							{errors.title && <ErrorLabel message={errors.title.message} />}
						</div>
					</div>
					<div className="form-row">
						<div className="form-element">
							<Input
								inputName="company"
								label={companyNameLabel || 'Company'}
								invalid={!!errors.company}
								valid={
									watchAllFields.company !== '' &&
									dirtyFields.company &&
									!errors.company
								}
								required
								register={register}
								rules={{
									required: 'This field is required',
									minLength: {
										value: 2,
										message:
											'Minimum required length for this field is 2 characters',
									},
								}}
							/>
							{errors.company && (
								<ErrorLabel message={errors.company.message} />
							)}
						</div>
					</div>
					<div className="form-row">
						<div className="form-element">
							<CityInput
								inputName="location"
								required
								register={register}
								label={registrationCityLabel || 'City'}
								placeholder={cityPlaceholder}
								invalid={!!errors.location}
								valid={
									watchAllFields.location !== '' &&
									dirtyFields.location &&
									!errors.location
								}
								isFormSubmitted={isSucceed}
								rules={{
									required: 'This field is required',
								}}
								setValue={setValue}
								clearErrors={clearErrors}
							/>
							{errors.location && (
								<ErrorLabel message={errors.location.message} />
							)}
						</div>
					</div>
					<div className="form-row">
						<div className="form-element">
							<Textarea
								id="bio"
								content={speakerBioTooltip}
								hasTooltip={speakerBioTooltip ? 'bio' : ''}
								inputName="bio"
								label={speakerBioLabel || 'Your bio'}
								placeholder={speakerBioPlaceholder}
								invalid={!!errors.bio}
								valid={
									watchAllFields.bio !== '' && dirtyFields.bio && !errors.bio
								}
								required
								rules={{
									required: 'This field is required',
									minLength: {
										value: 100,
										message:
											"Speaker's Bio should be from 100 till 1000 symbols",
									},
									maxLength: {
										value: 1000,
										message:
											"Speaker's Bio should be from 100 till 1000 symbols",
									},
								}}
								register={register}
							/>
							{errors.bio && <ErrorLabel message={errors.bio.message} />}
						</div>
						{/* {speakerBioTooltip && (
							<Tooltip id="bio" content={speakerBioTooltip} />
						)} */}
					</div>
					<div className="form-row form-row--gap">
						<div className="form-element">
							<label className="form-label" htmlFor="facebook">
								{speakerSocialMediaLabel || 'Socialmedia links'}
							</label>
							<div className="speaker-form__social-input-row">
								<InputSocial
									inputName="facebookLink"
									Icon={FacebookLogo}
									link="https://www.facebook.com/"
									placeholder={speakerFacebookLinkPlaceholder}
									invalid={!!errors.facebookLink}
									valid={
										watchAllFields.facebookLink !== '' &&
										dirtyFields.facebookLink &&
										!errors.facebookLink
									}
									register={register}
									rules={{
										pattern: {
											value: VALIDATION_PATTERNS.url,
											message: 'Entered value does not match url format',
										},
									}}
								/>
								{errors.facebookLink && (
									<ErrorLabel message={errors.facebookLink.message} />
								)}
							</div>
							<div className="speaker-form__social-input-row">
								<InputSocial
									inputName="linkedinLink"
									Icon={LinkedinLogo}
									link="https://www.linkedin.com/"
									placeholder={speakerLinkedinLinkPlaceholder}
									invalid={!!errors.linkedinLink}
									valid={
										watchAllFields.linkedinLink !== '' &&
										dirtyFields.linkedinLink &&
										!errors.linkedinLink
									}
									register={register}
									rules={{
										pattern: {
											value: VALIDATION_PATTERNS.url,
											message: 'Entered value does not match url format',
										},
									}}
								/>
								{errors.linkedinLink && (
									<ErrorLabel message={errors.linkedinLink.message} />
								)}
							</div>
							<div className="speaker-form__social-input-row">
								<InputSocial
									inputName="twitterLink"
									Icon={TwitterLogo}
									link="https://twitter.com/"
									placeholder={speakerTwitterLinkPlaceholder}
									invalid={!!errors.twitterLink}
									valid={
										watchAllFields.twitterLink !== '' &&
										dirtyFields.twitterLink &&
										!errors.twitterLink
									}
									register={register}
									rules={{
										pattern: {
											value: VALIDATION_PATTERNS.url,
											message: 'Entered value does not match url format',
										},
									}}
								/>
								{errors.twitterLink && (
									<ErrorLabel message={errors.twitterLink.message} />
								)}
							</div>
						</div>
					</div>
					<div className="form-row">
						<div className="form-element">
							<InputFile
								inputName="speakerPhoto"
								register={register}
								label={speakerPhotoLabel || 'Speakers photo'}
								required
								reset={resetPhoto}
								placeholder={speakerPhotoPlaceholder}
								bottomText={speakerPhotoBottomDescription}
								file={watchAllFields.speakerPhoto}
								rules={{
									required: 'This field is required',
									/* @ts-ignore */
									validate: {
										typeValidate: (fileList: FileList) =>
											fileList[0].type === 'image/png' ||
											fileList[0].type === 'image/jpeg' ||
											fileList[0].type === 'image/jpg',
										sizeValidate: (fileList: FileList) =>
											fileList[0].size < 10485760,
									},
								}}
							/>
							{errors.speakerPhoto &&
								errors.speakerPhoto.type === 'sizeValidate' && (
									<ErrorLabel message="Photo should be less than 10Mb" />
								)}
							{errors.speakerPhoto &&
								errors.speakerPhoto.type === 'typeValidate' && (
									<ErrorLabel message="File should be JPEG or PNG" />
								)}
							{errors.speakerPhoto && (
								<ErrorLabel message={errors.speakerPhoto.message} />
							)}
						</div>
					</div>
				</div>

				<div className="speaker-form__section">
					<h3 className="uikit-heading-lg speaker-form__heading speaker-form-heading">
						<span className="speaker-form-heading__left">
							<sup>1</sup>
							{speakerTalkTitle || 'Talk Info:'}
						</span>
					</h3>
					<div className="form-row">
						<div className="form-element">
							<Input
								content={speakerTalkNameTooltip}
								inputName="talkName"
								label={speakerTalkNameLabel || 'Talk name'}
								hasTooltip={speakerTalkNameTooltip ? 'talkName' : ''}
								invalid={!!errors.talkName}
								valid={
									watchAllFields.talkName !== '' &&
									dirtyFields.talkName &&
									!errors.talkName
								}
								required
								register={register}
								rules={{
									required: 'This field is required',
									minLength: {
										value: 2,
										message:
											'Minimum required length for this field is 2 characters',
									},
								}}
							/>
							{errors.talkName && (
								<ErrorLabel message={errors.talkName.message} />
							)}
						</div>
						{/* {speakerTalkNameTooltip && (
							<Tooltip id="talkName" content={speakerTalkNameTooltip} />
						)} */}
					</div>
					<div className="form-row">
						<div className="form-element form-element--double">
							<Select
								inputName="talkFormat"
								register={register}
								label={speakerFormatLabel || 'Format'}
								hasTooltip={speakerFormatTooltip ? 'talkFormat' : ''}
								options={speakerFormatValues}
								placeholder={speakerFormatPlaceholder}
								required
								rules={{
									required: 'This field is required',
								}}
							/>
							{errors.talkFormat && (
								<ErrorLabel message={errors.talkFormat.message} />
							)}
						</div>
						<div className="form-element form-element--double">
							<Select
								content={speakerLanguageTooltip}
								options={speakerLanguageValues}
								inputName="talkLanguage"
								hasTooltip={speakerLanguageTooltip ? 'talkLanguage' : ''}
								register={register}
								label={speakerLanguageLabel || 'Language'}
								required
								placeholder={speakerLanguagePlaceholder}
								rules={{
									required: 'This field is required',
								}}
							/>
							{errors.talkLanguage && (
								<ErrorLabel message={errors.talkLanguage.message} />
							)}
						</div>
						{/* {speakerLanguageTooltip && (
							<Tooltip
								id="talkLanguage"
								arrowPlacement="top-right"
								content={speakerLanguageTooltip}
							/>
						)} */}

						{/* Need html output right here instead of string */}
						{speakerFormatTooltip && (
							<Tooltip id="talkFormat" content={speakerFormatTooltip} />
						)}
					</div>
					<div className="form-row">
						<div className="form-element">
							<MultiSelect
								hasTooltip={speakerTrackTooltip ? 'talkTrack' : ''}
								label={speakerTrackLabel || 'Track'}
								inputName="talkTrack"
								required
								options={speakerTrackValues}
								placeholder={speakerTrackPlaceholder}
								rules={{
									required: 'This field is required',
								}}
								isFormSubmitted={isSucceed}
								register={register}
								setValue={setValue}
								clearErrors={clearErrors}
							/>
							{errors.talkTrack && (
								<ErrorLabel message={errors.talkTrack.message} />
							)}
						</div>
						{speakerTrackTooltip && (
							<Tooltip id="talkTrack" content={speakerTrackTooltip} />
						)}
					</div>
					<div className="form-row">
						<div className="form-element">
							<MultiSelect
								label={speakerAudienceLevelLabel || 'Audience level'}
								placeholder={speakerAudienceLevelPlaceholder}
								inputName="talkAudienceLevel"
								required
								options={speakerAudienceLevelValues}
								rules={{
									required: 'This field is required',
								}}
								isFormSubmitted={isSucceed}
								register={register}
								setValue={setValue}
								clearErrors={clearErrors}
							/>
							{errors.talkAudienceLevel && (
								<ErrorLabel message={errors.talkAudienceLevel.message} />
							)}
						</div>
					</div>
					<div className="form-row">
						<div className="form-element">
							<Textarea
								inputName="talkKeyPoints"
								label={speakerTalkKeyPointsLabel || 'Talk key points'}
								placeholder={speakerTalkKeyPointsPlaceholder}
								required
								invalid={!!errors.talkKeyPoints}
								valid={
									watchAllFields.talkKeyPoints !== '' &&
									dirtyFields.talkKeyPoints &&
									!errors.talkKeyPoints
								}
								rules={{
									required: 'This field is required',
									minLength: {
										value: 100,
										message:
											"Speaker's Talk key points should be from 100 till 1000 symbols",
									},
									maxLength: {
										value: 1000,
										message:
											"Speaker's Talk key points should be from 100 till 1000 symbols",
									},
								}}
								register={register}
							/>

							{errors.talkKeyPoints && (
								<ErrorLabel message={errors.talkKeyPoints.message} />
							)}
						</div>
					</div>
					<div className="form-row">
						<div className="form-element">
							<Textarea
								content={speakerTalkDescriptionTooltip}
								inputName="talkDescription"
								label={speakerTalkDescriptionLabel || 'Talk description'}
								placeholder={speakerTalkDescriptionPlaceholder}
								invalid={!!errors.talkDescription}
								hasTooltip={
									speakerTalkDescriptionTooltip ? 'talkDescription' : ''
								}
								valid={
									watchAllFields.talkDescription !== '' &&
									dirtyFields.talkDescription &&
									!errors.talkDescription
								}
								required
								rules={{
									required: 'This field is required',
									minLength: {
										value: 100,
										message:
											"Speaker's Talk description should be from 100 till 1000 symbols",
									},
									maxLength: {
										value: 1000,
										message:
											"Speaker's Talk description should be from 100 till 1000 symbols",
									},
								}}
								register={register}
							/>

							{errors.talkDescription && (
								<ErrorLabel message={errors.talkDescription.message} />
							)}
						</div>
						{speakerTalkDescriptionTooltip && (
							<Tooltip
								id="talkDescription"
								content={speakerTalkDescriptionTooltip}
							/>
						)}
					</div>
					<div className="form-row form-row--gap">
						<div className="form-element">
							<span className="form-label">
								{speakerCoSpeakerLabel ||
									'Do you have a co-speaker for this speech?'}
							</span>
							<Radio
								inputName="talkCoSpeaker"
								label="Yes"
								register={register}
							/>
							<Radio
								inputName="talkCoSpeaker"
								label="No"
								register={register}
								defaultChecked
							/>
						</div>
					</div>
				</div>
				{talkNumbers >= 2 && (
					<div className="speaker-form__section">
						<h3 className="uikit-heading-lg speaker-form__heading speaker-form-heading">
							<span className="speaker-form-heading__left">
								<sup>2</sup>
								{speakerTalkTitle || 'Talk Info:'}
							</span>
							{talkNumbers === 2 && (
								<span
									onClick={() => {
										removeTalkInfo();
									}}
									onKeyPress={() => {
										removeTalkInfo();
									}}
									role="button"
									tabIndex={0}
									className="uikit-label-small-caps speaker-form-heading__remove"
								>
									Remove 2d talk info
									<RemoveIcon />
								</span>
							)}
						</h3>
						<div className="form-row">
							<div className="form-element">
								<Input
									inputName="talkName2"
									label={speakerTalkNameLabel || 'Talk name'}
									invalid={!!errors.talkName2}
									hasTooltip={speakerTalkNameTooltip ? 'talkName2' : ''}
									valid={
										watchAllFields.talkName2 !== '' &&
										dirtyFields.talkName2 &&
										!errors.talkName2
									}
									required
									register={register}
									rules={{
										required: 'This field is required',
										minLength: {
											value: 2,
											message:
												'Minimum required length for this field is 2 characters',
										},
									}}
								/>
								{errors.talkName2 && (
									<ErrorLabel message={errors.talkName2.message} />
								)}
							</div>
							{speakerTalkNameTooltip && (
								<Tooltip id="talkName2" content={speakerTalkNameTooltip} />
							)}
						</div>
						<div className="form-row">
							<div className="form-element form-element--double">
								<Select
									inputName="talkFormat2"
									register={register}
									label={speakerFormatLabel || 'Format'}
									hasTooltip={speakerFormatTooltip ? 'talkFormat2' : ''}
									options={speakerFormatValues}
									placeholder={speakerFormatPlaceholder}
									required
									rules={{
										required: 'This field is required',
									}}
								/>
								{errors.talkFormat2 && (
									<ErrorLabel message={errors.talkFormat2.message} />
								)}
							</div>
							<div className="form-element form-element--double">
								<Select
									options={speakerLanguageValues}
									inputName="talkLanguage2"
									hasTooltip={speakerLanguageTooltip ? 'talkLanguage2' : ''}
									register={register}
									label={speakerLanguageLabel || 'Language'}
									required={speakerFormatTooltip !== ''}
									placeholder={speakerLanguagePlaceholder}
									rules={{
										required: 'This field is required',
									}}
								/>
							</div>
							{errors.talkLanguage2 && (
								<ErrorLabel message={errors.talkLanguage2.message} />
							)}

							{speakerLanguageTooltip && (
								<Tooltip
									id="talkLanguage2"
									arrowPlacement="top-right"
									content={speakerLanguageTooltip}
								/>
							)}

							{speakerFormatTooltip && (
								<Tooltip id="talkFormat2" content={speakerFormatTooltip} />
							)}
						</div>
						<div className="form-row">
							<div className="form-element">
								<MultiSelect
									hasTooltip={speakerTrackTooltip ? 'talkTrack2' : ''}
									label={speakerTrackLabel || 'Track'}
									inputName="talkTrack2"
									options={speakerTrackValues}
									required
									placeholder={speakerTrackPlaceholder}
									rules={{
										required: 'This field is required',
									}}
									isFormSubmitted={isSucceed}
									register={register}
									setValue={setValue}
									clearErrors={clearErrors}
								/>
								{errors.talkTrack2 && (
									<ErrorLabel message={errors.talkTrack2.message} />
								)}
							</div>
							{speakerTrackTooltip && (
								<Tooltip id="talkTrack2" content={speakerTrackTooltip} />
							)}
						</div>
						<div className="form-row">
							<div className="form-element">
								<MultiSelect
									label={speakerAudienceLevelLabel || 'Audience level'}
									placeholder={speakerAudienceLevelPlaceholder}
									inputName="talkAudienceLevel2"
									required
									options={speakerAudienceLevelValues}
									rules={{
										required: 'This field is required',
									}}
									isFormSubmitted={isSucceed}
									register={register}
									setValue={setValue}
									clearErrors={clearErrors}
								/>
								{errors.talkAudienceLevel2 && (
									<ErrorLabel message={errors.talkAudienceLevel2.message} />
								)}
							</div>
						</div>
						<div className="form-row">
							<div className="form-element">
								<Textarea
									inputName="talkKeyPoints2"
									label={speakerTalkKeyPointsLabel || 'Talk key points'}
									placeholder={speakerTalkKeyPointsPlaceholder}
									invalid={!!errors.talkKeyPoints2}
									valid={
										watchAllFields.talkKeyPoints2 !== '' &&
										dirtyFields.talkKeyPoints2 &&
										!errors.talkKeyPoints2
									}
									required
									rules={{
										required: 'This field is required',
									}}
									register={register}
								/>

								{errors.talkKeyPoints2 && (
									<ErrorLabel message={errors.talkKeyPoints2.message} />
								)}
							</div>
						</div>
						<div className="form-row">
							<div className="form-element">
								<Textarea
									inputName="talkDescription2"
									label={speakerTalkDescriptionLabel || 'Talk description'}
									placeholder={speakerTalkDescriptionPlaceholder}
									hasTooltip={
										speakerTalkDescriptionTooltip ? 'talkDescription2' : ''
									}
									invalid={!!errors.talkDescription2}
									valid={
										watchAllFields.talkDescription2 !== '' &&
										dirtyFields.talkDescription2 &&
										!errors.talkDescription2
									}
									required
									rules={{
										required: 'This field is required',
									}}
									register={register}
								/>

								{errors.talkDescription2 && (
									<ErrorLabel message={errors.talkDescription2.message} />
								)}
							</div>
							{speakerTalkDescriptionTooltip && (
								<Tooltip
									id="talkDescription2"
									content={speakerTalkDescriptionTooltip}
								/>
							)}
						</div>
						<div className="form-row form-row--gap">
							<div className="form-element">
								<span className="form-label">
									{speakerCoSpeakerLabel ||
										'Do you have a co-speaker for this speech?'}
								</span>
								<Radio
									inputName="talkCoSpeaker2"
									label="Yes"
									register={register}
								/>
								<Radio
									defaultChecked
									inputName="talkCoSpeaker2"
									label="No"
									register={register}
								/>
							</div>
						</div>
					</div>
				)}
				{talkNumbers === 3 && (
					<div className="speaker-form__section">
						<h3 className="uikit-heading-lg speaker-form__heading speaker-form-heading">
							<span className="speaker-form-heading__left">
								<sup>3</sup>
								{speakerTalkTitle || 'Talk Info:'}
							</span>
							<span
								onClick={() => {
									removeTalkInfo();
								}}
								onKeyPress={() => {
									removeTalkInfo();
								}}
								role="button"
								tabIndex={0}
								className="uikit-label-small-caps speaker-form-heading__remove"
							>
								Remove 3rd talk info
								<RemoveIcon />
							</span>
						</h3>
						<div className="form-row">
							<div className="form-element">
								<Input
									inputName="talkName3"
									label={speakerTalkNameLabel || 'Talk name'}
									invalid={!!errors.talkName3}
									hasTooltip={speakerTalkNameTooltip ? 'talkName3' : ''}
									valid={
										watchAllFields.talkName3 !== '' &&
										dirtyFields.talkName3 &&
										!errors.talkName3
									}
									required
									register={register}
									rules={{
										required: 'This field is required',
										minLength: {
											value: 2,
											message:
												'Minimum required length for this field is 2 characters',
										},
									}}
								/>
								{errors.talkName3 && (
									<ErrorLabel message={errors.talkName3.message} />
								)}
							</div>
							{speakerTalkNameTooltip && (
								<Tooltip id="talkName3" content={speakerTalkNameTooltip} />
							)}
						</div>
						<div className="form-row">
							<div className="form-element form-element--double">
								<Select
									inputName="talkFormat3"
									register={register}
									label={speakerFormatLabel || 'Format'}
									hasTooltip={speakerFormatTooltip ? 'talkFormat3' : ''}
									options={speakerFormatValues}
									placeholder={speakerFormatPlaceholder}
									required
									rules={{
										required: 'This field is required',
									}}
								/>
								{errors.talkFormat3 && (
									<ErrorLabel message={errors.talkFormat3.message} />
								)}
							</div>
							<div className="form-element form-element--double">
								<Select
									options={speakerLanguageValues}
									inputName="talkLanguage3"
									hasTooltip={speakerLanguageTooltip ? 'talkLanguage3' : ''}
									register={register}
									label={speakerLanguageLabel || 'Language'}
									required
									placeholder={speakerLanguagePlaceholder}
									rules={{
										required: 'This field is required',
									}}
								/>
							</div>
							{errors.talkLanguage3 && (
								<ErrorLabel message={errors.talkLanguage3.message} />
							)}
							{speakerLanguageTooltip && (
								<Tooltip
									id="talkLanguage3"
									arrowPlacement="top-right"
									content={speakerLanguageTooltip}
								/>
							)}

							{speakerFormatTooltip && (
								<Tooltip id="talkFormat3" content={speakerFormatTooltip} />
							)}
						</div>
						<div className="form-row">
							<div className="form-element">
								<MultiSelect
									hasTooltip={speakerTrackTooltip ? 'talkTrack3' : ''}
									label={speakerTrackLabel || 'Track'}
									inputName="talkTrack3"
									required
									options={speakerTrackValues}
									placeholder={speakerTrackPlaceholder}
									rules={{
										required: 'This field is required',
									}}
									isFormSubmitted={isSucceed}
									register={register}
									setValue={setValue}
									clearErrors={clearErrors}
								/>
								{errors.talkTrack3 && (
									<ErrorLabel message={errors.talkTrack3.message} />
								)}
							</div>
							{speakerTrackTooltip && (
								<Tooltip id="talkTrack3" content={speakerTrackTooltip} />
							)}
						</div>
						<div className="form-row">
							<div className="form-element">
								<MultiSelect
									label={speakerAudienceLevelLabel || 'Audience level'}
									placeholder={speakerAudienceLevelPlaceholder}
									inputName="talkAudienceLevel3"
									required
									options={speakerAudienceLevelValues}
									rules={{
										required: 'This field is required',
									}}
									isFormSubmitted={isSucceed}
									register={register}
									setValue={setValue}
									clearErrors={clearErrors}
								/>
								{errors.talkAudienceLevel3 && (
									<ErrorLabel message={errors.talkAudienceLevel3.message} />
								)}
							</div>
						</div>
						<div className="form-row">
							<div className="form-element">
								<Textarea
									inputName="talkKeyPoints3"
									label={speakerTalkKeyPointsLabel || 'Talk key points'}
									placeholder={speakerTalkKeyPointsPlaceholder}
									invalid={!!errors.talkKeyPoints3}
									valid={
										watchAllFields.talkKeyPoints3 !== '' &&
										dirtyFields.talkKeyPoints3 &&
										!errors.talkKeyPoints3
									}
									required
									rules={{
										required: 'This field is required',
									}}
									register={register}
								/>

								{errors.talkKeyPoints3 && (
									<ErrorLabel message={errors.talkKeyPoints3.message} />
								)}
							</div>
						</div>
						<div className="form-row">
							<div className="form-element">
								<Textarea
									inputName="talkDescription3"
									label={speakerTalkDescriptionLabel || 'Talk description'}
									placeholder={speakerTalkDescriptionPlaceholder}
									invalid={!!errors.talkDescription3}
									hasTooltip={
										speakerTalkDescriptionTooltip ? 'talkDescription3' : ''
									}
									valid={
										watchAllFields.talkDescription3 !== '' &&
										dirtyFields.talkDescription3 &&
										!errors.talkDescription3
									}
									required
									rules={{
										required: 'This field is required',
									}}
									register={register}
								/>

								{errors.talkDescription3 && (
									<ErrorLabel message={errors.talkDescription3.message} />
								)}
							</div>
							{speakerTalkDescriptionTooltip && (
								<Tooltip
									id="talkDescription3"
									content={speakerTalkDescriptionTooltip}
								/>
							)}
						</div>
						<div className="form-row form-row--gap">
							<div className="form-element">
								<span className="form-label">
									{speakerCoSpeakerLabel ||
										'Do you have a co-speaker for this speech?'}
								</span>
								<Radio
									inputName="talkCoSpeaker3"
									label="Yes"
									register={register}
								/>
								<Radio
									defaultChecked
									inputName="talkCoSpeaker3"
									label="No"
									register={register}
								/>
							</div>
						</div>
					</div>
				)}
				{talkNumbers < 3 && (
					<div className="speaker-form__section">
						<div className="form-row form-row--gap">
							<div
								onClick={() => {
									setTalkNumbers(talkNumbers + 1);
								}}
								onKeyPress={() => {
									setTalkNumbers(talkNumbers + 1);
								}}
								role="button"
								tabIndex={0}
								className="form-element"
							>
								<ButtonPrimary
									type="button"
									icon="add"
									submitButtonLabel={
										speakerAddTalkButtonLabel || 'Add Another Talk'
									}
								/>
							</div>
						</div>
					</div>
				)}
				<div className="speaker-form__section">
					<div className="form-row form-row--gap">
						<div className="form-element">
							<Checkbox
								register={register}
								inputName="privacy"
								required
								rules={{ required: 'This field is required' }}
								label={privacyPolicyLabelText}
							/>
							{errors.privacy && (
								<ErrorLabel message={errors.privacy.message} />
							)}
						</div>
					</div>
					<div className="form-row form-row--gap">
						<div className="form-element">
							<Checkbox
								register={register}
								inputName="codeConduct"
								required
								rules={{ required: 'This field is required' }}
								label={speakerCodeOfConductAndSpeakerContractAgreementLabel}
							/>
							{errors.codeConduct && (
								<ErrorLabel message={errors.codeConduct.message} />
							)}
						</div>
					</div>
					<div className="form-row form-row--gap-lg form-row--push-right">
						<ButtonPrimary
							id="submit_speaker"
							disabled={!isFull || Object.keys(errors).length !== 0}
							type="submit"
							submitButtonLabel={
								isSending ? 'Sending' : submitButtonLabel || 'Submit'
							}
						/>
					</div>
				</div>
			</form>
			{isSucceed && (
				<SuccessPopup
					title="Thank you for submitting the application form."
					content="The call for papers will be closed on November, 6.<br />If you are late this year, please, contact us anyway&#x1F642; We'll save you for our future events."
					closePopup={async () => {
						onClickHandler();
					}}
					isActive={isSucceed}
				/>
			)}
		</>
	);
}

export default SpeakerForm;
